import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { useSeries3UpgradeContext } from '../../../contexts/Series3UpgradeContext';
import useSubscriptionProducts from '../../../hooks/useSubscriptionProducts';
import { cartActions as series3UpgradeCartActions } from '../../../reducers/series3UpgradeCart';
import * as types from '../../../types';
import Series3CollarDetails from '../../ProductDetails/Series3CollarDetails';
import { useLocale } from '../../../contexts/LocaleContext';
import { SERIES_3_UPGRADE_V2_COUPON_CODE } from '../../Series3UpgradeV2/Series3UpgradePDPV2';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../../lib/growthbook';

export default function UpgradeProductSelector() {
  const { offeredPlan, series3Collar } = useSeries3UpgradeContext();
  const dispatch = useDispatch();
  const history = useHistory();
  const { moduleId } = useParams<{ moduleId: string }>();
  const { currency } = useLocale();
  const subscriptionProducts = useSubscriptionProducts(types.series3PlusCollarId);
  const growthBookEnabled = useIsGrowthBookEnabled();
  const isBfcmUpgradeEnabled =
    growthBookEnabled && getFiGrowthBook().getFeatureValue<boolean>('ecom-bfcm-upgrade', false);

  // If the user is coming from the UpgradeSinglePetSplash page, they may have already selected a plan there.
  let preselectedPlanSku = useSelector(
    (state: types.AppState) => state.series3UpgradeShop.upgradePetContext.preSelectedSubscriptionSku,
  );
  if (isBfcmUpgradeEnabled) {
    preselectedPlanSku = subscriptionProducts.filter(
      (subscriptionProduct) => subscriptionProduct.renewalMonths === 12,
    )[0].sku;
  }

  // The backend gives us an offered plan for a particular module, but we want to consider that to be the
  // _minimum_ plan that the user can upgrade to. They are free to choose a plan with a longer duration than the
  // offered plan if they'd like.
  const offeredPlanRenewalMonths = offeredPlan?.renewalMonths ?? 0;

  const availableSubscriptions = subscriptionProducts.filter((subscriptionProduct) => {
    return subscriptionProduct.renewalMonths >= offeredPlanRenewalMonths;
  });

  return (
    <Series3CollarDetails
      availableSubscriptions={availableSubscriptions}
      addBundleCartItem={(variantSku, subscriptionSku) => {
        if (isBfcmUpgradeEnabled) {
          dispatch(series3UpgradeCartActions.addCoupon(SERIES_3_UPGRADE_V2_COUPON_CODE));
        }
        dispatch(
          series3UpgradeCartActions.addUpgradeCartItem({
            sku: variantSku,
            subscriptionSku: subscriptionSku,
            moduleId,
            currency,
          }),
        );

        history.push(AppPaths.Series3Upgrade.Bag);
      }}
      hideSubscriptionOptions={!!preselectedPlanSku && isBfcmUpgradeEnabled}
      priceInCents={series3Collar.priceInCents}
      preselectedSubscriptionSku={preselectedPlanSku ?? offeredPlan?.sku}
      productName={series3Collar.name}
      variants={series3Collar.variants}
      isUpgradePath={true}
    />
  );
}
